import React from "react"

import { Permission, PERMISSIONS } from "constants/permissions"

import { ReactComponent as ActivityIcon } from "../../assets/icons/activity.svg"
import { ReactComponent as BuildingIcon } from "../../assets/icons/building.svg"
import { ReactComponent as ChartIcon } from "../../assets/icons/chart.svg"
import { ReactComponent as CreditCardIcon } from "../../assets/icons/credit-card.svg"
import { ReactComponent as DashboardIcon } from "../../assets/icons/dashboard.svg"
import { ReactComponent as FileIcon } from "../../assets/icons/file.svg"
import { ReactComponent as FileCheckIcon } from "../../assets/icons/file-check.svg"
import { ReactComponent as GamepadIcon } from "../../assets/icons/gamepad.svg"
import { ReactComponent as GiftIcon } from "../../assets/icons/gift.svg"
import { ReactComponent as GridIcon } from "../../assets/icons/grid.svg"
import { ReactComponent as MegaphoneIcon } from "../../assets/icons/megaphone.svg"
import { ReactComponent as PrizeIcon } from "../../assets/icons/prize.svg"
import { ReactComponent as ReceiptIcon } from "../../assets/icons/receipt.svg"
import { ReactComponent as SettingsIcon } from "../../assets/icons/settings.svg"
import { ReactComponent as ShieldIcon } from "../../assets/icons/shield.svg"
import { ReactComponent as StackIcon } from "../../assets/icons/stack.svg"
import { ReactComponent as TargetIcon } from "../../assets/icons/target.svg"
import { ReactComponent as TicketIcon } from "../../assets/icons/ticket.svg"
import { ReactComponent as UsersIcon } from "../../assets/icons/users.svg"
import { ReactComponent as WalletIcon } from "../../assets/icons/wallet2.svg"
import { URL } from "../../constants/api/requests"

export type NavBadge = "withdrawals" | "kyc" | "review"

export type NavLinkItem = {
  type: "link"
  url: string
  label: string
  icon: React.FC<any>
  permission?: Permission
  badge?: NavBadge
  // D01: only shown when the tenant's authoritative bonus catalog mode matches (or is unresolved) —
  // so exactly one of the legacy Bonuses / Bonus V2 pages appears in the sidebar per tenant.
  bonusCatalogGate?: "legacy" | "v2"
}

export type NavGroupItem = {
  type: "group"
  key: "players" | "payments" | "marketing" | "content" | "tournaments" | "system"
  label: string
  icon: React.FC<any>
  permission?: Permission
  items: NavLinkItem[]
  badge?: NavBadge
}

export type NavItem = NavLinkItem | NavGroupItem

export const NAV_ITEMS: NavItem[] = [
  {
    type: "link",
    url: URL.DASHBOARD,
    label: "sidebar.dashboard",
    icon: DashboardIcon,
    permission: PERMISSIONS.PAGE_DASHBOARD
  },

  {
    type: "group",
    key: "players",
    label: "sidebar.players",
    icon: UsersIcon,
    badge: "kyc",
    items: [
      {
        type: "link",
        url: URL.USERS,
        label: "sidebar.allPlayers",
        icon: UsersIcon,
        permission: PERMISSIONS.PAGE_USERS
      },
      {
        type: "link",
        url: URL.TAGS,
        label: "sidebar.segmentation",
        icon: TargetIcon,
        permission: PERMISSIONS.PAGE_TAGS
      },
      {
        type: "link",
        url: URL.KYC_FILES,
        label: "sidebar.kycFiles",
        icon: FileCheckIcon,
        permission: PERMISSIONS.PAGE_KYC,
        badge: "kyc"
      },
      {
        type: "link",
        url: URL.RISK_DASHBOARD,
        label: "Risk & Fraud",
        icon: ShieldIcon,
        permission: PERMISSIONS.PAGE_RISK,
        badge: "review"
      }
    ]
  },

  {
    type: "group",
    key: "payments",
    label: "sidebar.payments",
    icon: CreditCardIcon,
    badge: "withdrawals",
    items: [
      {
        type: "link",
        url: URL.TRANSACTIONS,
        label: "sidebar.transactions",
        icon: ReceiptIcon,
        permission: PERMISSIONS.PAGE_TRANSACTIONS
      },
      {
        type: "link",
        url: URL.BANK_TRANSFER,
        label: "sidebar.withdrawals",
        icon: WalletIcon,
        permission: PERMISSIONS.PAGE_BANK_TRANSFERS,
        badge: "withdrawals"
      },
      {
        type: "link",
        url: URL.BALANCE,
        label: "sidebar.pspBalances",
        icon: BuildingIcon,
        permission: PERMISSIONS.PAGE_BALANCE
      }
    ]
  },

  {
    type: "group",
    key: "marketing",
    label: "sidebar.marketing",
    icon: MegaphoneIcon,
    items: [
      {
        type: "link",
        url: URL.BONUSES,
        label: "sidebar.bonuses",
        icon: GiftIcon,
        permission: PERMISSIONS.PAGE_BONUSES,
        // D01: hidden once the tenant resolves to the bonus-service (v2) catalog.
        bonusCatalogGate: "legacy"
      },
      {
        type: "link",
        url: URL.BONUS2_ADMIN,
        label: "sidebar.bonusV2",
        icon: GiftIcon,
        permission: PERMISSIONS.PAGE_BONUS2,
        // D01: only on tenants whose authoritative catalog is bonus-service (v2). Catalog-mode is the
        // authority (`bonus_catalog` from /admin/tenant-mode); unresolved = legacy.
        bonusCatalogGate: "v2"
      },
      {
        type: "link",
        url: URL.TOKEN_ADMIN,
        label: "sidebar.token",
        icon: GiftIcon,
        permission: PERMISSIONS.PAGE_TOKEN
        // No catalog gate. Unlike the bonus links there is no legacy/v2 pair to
        // choose between — $TOKEN exists only in the microservice. The per-tenant
        // feature gate lives on the backend, and the page renders an explicit
        // "not enabled for this tenant" state rather than being hidden: an admin
        // with the permission should be able to see WHY there is nothing there.
      },
      {
        type: "link",
        url: URL.PROMO_CODES,
        label: "sidebar.promoCodes",
        icon: TicketIcon,
        permission: PERMISSIONS.PAGE_PROMO_CODES,
        // Bonus V2 has its own Promo Codes tab inside the Bonus V2 admin page — hide this legacy
        // (V1) one on the same tenants that hide the legacy Bonuses page, so only one promocode
        // tab is ever active per tenant.
        bonusCatalogGate: "legacy"
      },
      {
        type: "link",
        url: URL.REFERRALS,
        label: "sidebar.affiliates",
        icon: UsersIcon,
        permission: PERMISSIONS.PAGE_REFERRALS
      },
      {
        type: "link",
        url: URL.S2S_POSTBACKS,
        label: "sidebar.s2sPostbacks",
        icon: TargetIcon,
        permission: PERMISSIONS.PAGE_POSTBACKS
      },
      {
        type: "link",
        url: URL.BETBY_BONUSES,
        label: "sidebar.betbyBonuses",
        icon: GiftIcon,
        permission: PERMISSIONS.PAGE_BETBY_BONUSES
      },
      {
        type: "link",
        url: URL.TOURNAMENTS,
        label: "sidebar.tournaments",
        icon: PrizeIcon,
        permission: PERMISSIONS.PAGE_TOURNAMENTS
      },
      {
        type: "link",
        url: URL.CHALLENGES,
        label: "sidebar.challenges",
        icon: PrizeIcon,
        permission: PERMISSIONS.PAGE_CHALLENGES
      }
    ]
  },

  {
    type: "group",
    key: "content",
    label: "sidebar.content",
    icon: GridIcon,
    items: [
      {
        type: "link",
        url: URL.CMS_SEO,
        label: "sidebar.cmsPages",
        icon: FileIcon,
        permission: PERMISSIONS.PAGE_CMS
      },
      {
        type: "link",
        url: URL.CMS,
        label: "sidebar.bannersAndTexts",
        icon: StackIcon,
        permission: PERMISSIONS.PAGE_CMS
      },
      {
        type: "link",
        url: URL.GAME_MANAGEMENT,
        label: "sidebar.games",
        icon: GamepadIcon,
        permission: PERMISSIONS.PAGE_GAME_MANAGEMENT
      }
    ]
  },
  {
    type: "group",
    key: "system",
    label: "sidebar.system",
    icon: SettingsIcon,
    items: [
      {
        type: "link",
        url: URL.SETTINGS,
        label: "sidebar.settings",
        icon: SettingsIcon,
        permission: PERMISSIONS.PAGE_SETTINGS
      },
      {
        type: "link",
        url: URL.ADMINS,
        label: "sidebar.adminUsers",
        icon: ShieldIcon,
        permission: PERMISSIONS.PAGE_ADMINS
      },
      {
        type: "link",
        url: URL.ROLE_MANAGEMENT,
        label: "sidebar.roleManagement",
        icon: SettingsIcon,
        permission: PERMISSIONS.ROLE_MANAGEMENT_EDIT
      },
      {
        type: "link",
        url: URL.GGR_MONTHLY_REPORT,
        label: "sidebar.ggrReport",
        icon: ChartIcon,
        permission: PERMISSIONS.PAGE_GGR_REPORT
      },
      {
        type: "link",
        url: URL.ACTIVITY,
        label: "sidebar.activityLogs",
        icon: ActivityIcon,
        permission: PERMISSIONS.PAGE_ACTIVITY
      }
    ]
  }
]
